/**
 * Left navigation of the settings (Einstellungen 01, A2): two groups. Only "Benachrichtigungen" and
 * "Sicherheit" are built in M2; the other entries show the empty state until their milestones
 * (Einstellungen 04–14 in M3, 01–03 and 18–29 in M8).
 */

export interface SettingsNavItem {
  key: string;
  label: string;
  href: string;
  /** Route segment under /settings (or /settings/organization). */
  built: boolean;
  /** Further paths that belong to the entry („Marke und Rechtstexte" also covers the legal texts page). */
  also?: readonly string[] | undefined;
}

export interface SettingsNavGroup {
  key: 'mine' | 'organization';
  label: string;
  items: SettingsNavItem[];
}

export const settingsGroups: readonly SettingsNavGroup[] = [
  {
    key: 'mine',
    label: 'Meine Einstellungen',
    items: [
      { key: 'profile', label: 'Mein Profil', href: '/settings/profile', built: true },
      { key: 'connections', label: 'Verbindungen', href: '/settings/connections', built: true },
      {
        key: 'availability',
        label: 'Verfügbarkeit und Termintypen',
        href: '/settings/availability',
        built: true,
      },
      { key: 'notifications', label: 'Benachrichtigungen', href: '/settings/notifications', built: true },
      { key: 'security', label: 'Sicherheit', href: '/settings/security', built: true },
      { key: 'billing', label: 'Abrechnung', href: '/settings/billing', built: true },
    ],
  },
  {
    key: 'organization',
    label: 'Organisation',
    items: [
      {
        key: 'brand',
        label: 'Marke und Rechtstexte',
        href: '/settings/organization/brand',
        built: true,
        also: ['/settings/organization/legal-texts'],
      },
      { key: 'domain', label: 'Domain und Subdomain', href: '/settings/organization/domain', built: true },
      {
        key: 'telephony',
        label: 'Telefonie und WhatsApp-Versand',
        href: '/settings/organization/telephony',
        built: true,
      },
      {
        key: 'integrations',
        label: 'Integrationen',
        href: '/settings/organization/integrations',
        built: true,
      },
      { key: 'users', label: 'Nutzer und Rollen', href: '/settings/organization/users', built: true },
      {
        key: 'permissions',
        label: 'Rechte und Rollen',
        href: '/settings/organization/permissions',
        built: true,
      },
      {
        key: 'pipelines',
        label: 'Pipelines und Lifecycle',
        href: '/settings/organization/pipelines',
        built: true,
      },
      { key: 'referrers', label: 'Tippgeber', href: '/settings/organization/referrers', built: true },
      { key: 'modules', label: 'Module', href: '/settings/organization/modules', built: true },
      { key: 'tree', label: 'Organisationsbaum', href: '/settings/organization/tree', built: true },
      { key: 'commission', label: 'Provisionssätze', href: '/settings/organization/commission', built: true },
      {
        key: 'commission-financing',
        label: 'Provision Finanzierungen',
        href: '/settings/organization/commission-financing',
        built: true,
      },
      {
        key: 'calculation-assumptions',
        label: 'Rechenannahmen Selbstauskunft',
        href: '/settings/organization/calculation-assumptions',
        built: true,
      },
      { key: 'deals', label: 'Deals', href: '/settings/organization/deals', built: true },
      {
        key: 'organization-billing',
        label: 'Abrechnung der Organisation',
        href: '/settings/organization/billing',
        built: true,
      },
    ],
  },
];

/** Every settings item with its group, for the empty pages and the mobile header. */
export function findSettingsItem(
  pathname: string,
): { group: SettingsNavGroup; item: SettingsNavItem } | null {
  for (const group of settingsGroups) {
    for (const item of group.items) {
      if (pathname === item.href || pathname.startsWith(`${item.href}/`)) return { group, item };
      if (item.also?.some((path) => pathname === path || pathname.startsWith(`${path}/`)))
        return { group, item };
    }
  }
  return null;
}

/** Mobile labels of EM1 are shorter than the desktop ones. */
export const mobileLabels: Record<string, string> = {
  availability: 'Verfügbarkeit',
};
